(() => {
  const messages = JSON.parse(document.querySelector('#StorefrontStrings')?.textContent || '{}');
  const message = (key, fallback, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), messages[key] || fallback);
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || !window.fetch || !drawer.showModal) return;
  const root = (window.Shopify?.routes?.root || drawer.dataset.cartRoot || '/').replace(/\/?$/, '/');
  const find = selector => drawer.querySelector(selector);
  let currency = drawer.dataset.cartCurrency || 'EUR';
  const money = amount => new Intl.NumberFormat(drawer.dataset.cartLocale || 'nl', { style: 'currency', currency }).format(amount / 100);
  let busy = false;
  let lastTrigger;
  let currentCart;
  let closeTimer;
  const reducedMotion = () => document.body.dataset.motion === 'false' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  function safeUrl(value, image = false) {
    try { const url = new URL(value, location.origin); return ['https:', 'http:'].includes(url.protocol) && (image || url.origin === location.origin) ? url.href : '#'; } catch { return '#'; }
  }
  function setError(message = '') {
    find('[data-cart-error-message]').textContent = message;
    find('[data-cart-error]').hidden = !message;
  }
  function setBusy(value) {
    busy = value;
    find('[data-cart-items]').setAttribute('aria-busy', String(value));
    drawer.querySelectorAll('[data-change-quantity], [name="checkout"], .cart-drawer__extra-form button').forEach(button => { button.disabled = value || button.dataset.limitReached === 'true'; });
  }
  async function request(endpoint, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(root + endpoint, { credentials: 'same-origin', ...options, signal: controller.signal, headers: { Accept: 'application/json', ...(options.headers || {}) } });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : message('request_failed', 'Dat lukte even niet. Controleer je tas voordat je opnieuw probeert.'));
      return data;
    } finally { clearTimeout(timer); }
  }
  function renderCart(cart) {
    currentCart = cart;
    currency = cart.currency || currency;
    find('[data-drawer-count]').textContent = `(${cart.item_count})`;
    find('[data-cart-total]').textContent = money(cart.total_price);
    const discounts = find('[data-cart-discounts]');
    discounts.replaceChildren();
    (cart.cart_level_discount_applications || []).forEach(discount => {
      const row = node('li'); row.append(node('span', '', discount.title), node('span', '', `−${money(discount.total_allocated_amount)}`)); discounts.append(row);
    });
    discounts.hidden = !discounts.children.length;
    find('[data-cart-empty]').hidden = cart.item_count > 0;
    find('[data-cart-footer]').hidden = cart.item_count === 0;
    const extra = find('[data-cart-extra]');
    if (extra) extra.hidden = cart.item_count === 0 || cart.items.some(item => String(item.product_id) === extra.dataset.productId);
    const items = find('[data-cart-items]');
    items.replaceChildren();
    cart.items.forEach(item => {
      const article = node('article', 'drawer-item');
      const imageLink = node('a', 'drawer-item__image');
      imageLink.href = safeUrl(item.url);
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      if (item.image) {
        const image = node('img'); image.src = safeUrl(item.image, true); image.alt = ''; image.width = 88; image.height = 112; imageLink.append(image);
      }
      const details = node('div', 'drawer-item__details');
      const title = node('a', 'drawer-item__title', item.product_title || item.title); title.href = safeUrl(item.url); details.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') details.append(node('p', 'drawer-item__variant', item.variant_title));
      if (item.selling_plan_allocation?.selling_plan?.name) details.append(node('p', 'drawer-item__variant', item.selling_plan_allocation.selling_plan.name));
      Object.entries(item.properties || {}).filter(([key,value]) => !key.startsWith('_') && value).forEach(([key,value]) => details.append(node('p', 'drawer-item__variant', `${key}: ${value}`)));
      const price = node('p', 'drawer-item__price');
      const finalPrice = item.final_line_price ?? item.line_price;
      if (item.original_line_price > finalPrice) {
        const original = node('s'); original.append(node('span', 'visually-hidden', message('original', 'Oorspronkelijk ')), document.createTextNode(money(item.original_line_price))); price.append(original);
      }
      price.append(node('span', '', money(finalPrice))); details.append(price);
      if (item.line_level_discount_allocations?.length) {
        const list = node('ul', 'drawer-item__discounts'); list.setAttribute('role', 'list');
        item.line_level_discount_allocations.forEach(discount => list.append(node('li', '', `${discount.discount_application.title} (−${money(discount.amount)})`))); details.append(list);
      }
      const controls = node('div', 'drawer-item__controls');
      const quantity = node('div', 'drawer-item__quantity');
      const step = Number(item.quantity_rule?.increment) || 1;
      const min = Number(item.quantity_rule?.min) || 1;
      const minus = node('button', '', '−'); minus.type = 'button'; minus.dataset.changeQuantity = ''; minus.setAttribute('aria-label', message('less', 'Minder [product]', { product: item.product_title || item.title }));
      minus.addEventListener('click', () => changeQuantity(item.key, item.quantity - step < min ? 0 : item.quantity - step));
      const count = node('output', '', String(item.quantity)); count.setAttribute('aria-label', message('quantity', 'Aantal'));
      const plus = node('button', '', '+'); plus.type = 'button'; plus.dataset.changeQuantity = ''; plus.setAttribute('aria-label', message('more', 'Meer [product]', { product: item.product_title || item.title }));
      plus.dataset.limitReached = String(item.quantity_rule?.max != null && item.quantity + step > Number(item.quantity_rule.max));
      plus.addEventListener('click', () => changeQuantity(item.key, item.quantity + step));
      quantity.append(minus, count, plus);
      const remove = node('button', 'drawer-item__remove', message('remove', 'Verwijderen')); remove.type = 'button'; remove.dataset.changeQuantity = ''; remove.setAttribute('aria-label', message('remove_product', 'Verwijder [product]', { product: item.product_title || item.title })); remove.addEventListener('click', () => changeQuantity(item.key, 0));
      controls.append(quantity, remove); details.append(controls); article.append(imageLink, details); items.append(article);
    });
    document.querySelectorAll('.bag-link').forEach(link => {
      link.setAttribute('aria-label', message('bag_count', 'Winkelmand, [count] artikelen', { count: cart.item_count }));
      let badge = link.querySelector('.cart-count');
      if (!badge) { badge = node('span', 'cart-count'); link.append(badge); }
      badge.textContent = String(cart.item_count); badge.hidden = cart.item_count === 0;
    });
    const threshold = Number(drawer.dataset.freeShipping) * 100;
    const shipping = find('[data-cart-shipping]');
    shipping.hidden = !threshold || cart.item_count === 0 || currency !== drawer.dataset.cartCurrency;
    if (!shipping.hidden) {
      const remainder = Math.max(0, threshold - cart.total_price);
      find('[data-cart-shipping-label]').textContent = remainder ? message('shipping_remaining', 'Nog [amount] tot gratis verzending.', { amount: money(remainder) }) : message('shipping_reached', 'Je tas komt in aanmerking voor gratis verzending.');
      const percent = Math.min(100, Math.round(cart.total_price / threshold * 100));
      const progress = find('.cart-drawer__progress'); progress.setAttribute('aria-valuenow', String(percent)); progress.querySelector('span').style.transform = `scaleX(${percent / 100})`;
    }
    setBusy(busy);
  }
  function closeDrawer() {
    if (!drawer.open || drawer.hasAttribute('data-closing')) return;
    if (reducedMotion()) { drawer.close(); return; }
    drawer.setAttribute('data-closing', '');
    closeTimer = setTimeout(() => drawer.close(), 160);
  }
  function showDrawer(trigger) {
    clearTimeout(closeTimer);
    drawer.removeAttribute('data-closing');
    if (!drawer.open) lastTrigger = trigger || document.activeElement;
    document.querySelectorAll('dialog[open]').forEach(dialog => { if (dialog !== drawer) dialog.close(); });
    if (!drawer.open) drawer.showModal();
    document.body.classList.add('cart-is-open');
  }
  async function openCart(trigger) {
    if (busy) return;
    setBusy(true); showDrawer(trigger); setError(); find('[data-cart-status]').textContent = message('loading', 'Je tas wordt opgehaald…');
    try { renderCart(await request('cart.js')); find('[data-cart-status]').textContent = ''; }
    catch { setError(message('load_failed', 'Je tas kon niet worden geladen. Bekijk je winkelmand om verder te gaan.')); find('[data-cart-status]').textContent = ''; find('[data-cart-footer]').hidden = true; }
    finally { setBusy(false); }
  }
  async function changeQuantity(key, quantity) {
    if (busy) return;
    const focused = document.activeElement;
    const oldIndex = currentCart?.items.findIndex(item => item.key === key) ?? 0;
    const actionLabel = focused?.getAttribute('aria-label');
    setBusy(true); setError();
    try {
      renderCart(await request('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) }));
      find('[data-cart-status]').textContent = quantity === 0 ? message('removed', 'Vondst verwijderd.') : message('updated', 'Aantal bijgewerkt.');
      setBusy(false);
      const row = find('[data-cart-items]').children[Math.min(oldIndex, currentCart.items.length - 1)];
      const replacement = row && [...row.querySelectorAll('button')].find(button => button.getAttribute('aria-label') === actionLabel);
      (replacement || row?.querySelector('a') || find('[data-cart-close]')).focus({ preventScroll: true });
    } catch (error) {
      // Shopify can change stock quantities even when a request returns an error.
      // Re-read the cart; never retry the mutation automatically.
      try { renderCart(await request('cart.js')); } catch { find('[data-cart-footer]').hidden = true; }
      setError(error instanceof TypeError || error.name === 'AbortError' ? message('change_failed', 'Wijzigen niet bevestigd. Controleer je winkelmand voordat je opnieuw probeert.') : error.message);
    }
    finally { setBusy(false); }
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('.bag-link');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // The full cart has its own updates[] form. Keep a single editing surface
    // there so a stale form cannot overwrite changes made in the drawer.
    if (document.querySelector('.cart-page')) return;
    event.preventDefault(); openCart(trigger);
  });
  find('[data-cart-close]').addEventListener('click', closeDrawer);
  drawer.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
  drawer.addEventListener('close', () => { clearTimeout(closeTimer); drawer.removeAttribute('data-closing'); document.body.classList.remove('cart-is-open'); lastTrigger?.focus({ preventScroll: true }); });
  drawer.addEventListener('click', event => {
    if (event.target !== drawer) return;
    const rect = drawer.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right) closeDrawer();
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.product-form')) return;
    event.preventDefault();
    if (busy || form.dataset.cartBusy === 'true') return;
    form.dataset.cartBusy = 'true';
    const buttons = [...document.querySelectorAll('button[type="submit"]')].filter(button => button.form === form);
    const states = buttons.map(button => button.disabled);
    const data = new FormData(form);
    data.delete('cozy_variant');
    const inputs = [...form.querySelectorAll('select, input:not([type=hidden])')];
    const inputStates = inputs.map(input => input.disabled);
    inputs.forEach(input => { input.disabled = true; });
    buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    setBusy(true);
    form.querySelector('.product-cart-error')?.remove();
    try {
      await request('cart/add.js', { method: 'POST', body: data });
      showDrawer(event.submitter); setError();
      renderCart(await request('cart.js'));
      find('[data-cart-status]').textContent = message('added', 'Toegevoegd aan je tas.');
    } catch (error) {
      const text = error instanceof TypeError || error.name === 'AbortError' ? message('add_failed', 'Toevoegen niet bevestigd. Controleer je tas voordat je opnieuw probeert.') : error.message;
      const errorMessage = node('p', 'product-cart-error', text);
      errorMessage.setAttribute('role', 'alert'); form.append(errorMessage);
      try {
        const cart = await request('cart.js'); renderCart(cart);
        if (cart.item_count || drawer.open) { showDrawer(event.submitter); setError(text); }
      } catch {
        if (drawer.open) { find('[data-cart-footer]').hidden = true; setError(message('confirm_failed', 'Je tas kon niet worden bevestigd. Bekijk je winkelmand voordat je opnieuw probeert.')); }
      }
    } finally {
      form.dataset.cartBusy = 'false';
      inputs.forEach((input, index) => { input.disabled = inputStates[index]; });
      buttons.forEach((button, index) => { button.disabled = states[index]; button.removeAttribute('aria-busy'); });
      setBusy(false);
    }
  });
})();
